import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes, { any } from 'prop-types';

import Img from '../../../../Img';
import ResultCard from '../ResultCard';
import useIsMobile from '../../../../hooks/useIsMobile';
import { useStatsig } from '../../../../hooks/useStatsig';
import { usePlayerSavedStatusBatch } from '../../../../SavedLists/usePlayerSavedStatusBatch';
import { mockData } from '../../mockData';
import { LeaderboardParamValues, LeaderboardTabTypes } from '../../constants';

import styles from '../../localizedRankings.scss';

const SINGLES = 'singles';
const DOUBLES = 'doubles';

const collectDistinctPlayerIds = (...playerLists) => {
    const ids = new Set();
    playerLists.forEach((list) => {
        (list || []).forEach((player) => {
            if (player?.playerId != null) {
                ids.add(player.playerId);
            }
        });
    });
    return [...ids];
};

// Helper to compare player IDs (handles both string and number)
const isSamePlayer = (playerId1, playerId2) => {
    if (!playerId1 || !playerId2) {
        return false;
    }
    return String(playerId1) === String(playerId2);
};

const ResultList = ({ results, isPowerUser, currentTab, rankingType, showDoubles, showSingles }) => {
    const { singles, doubles } = results || {};
    const { isMobileWidth } = useIsMobile();
    const currentProfile = useSelector((state) => state.profile?.data);
    const currentUserPlayerId = useSelector((state) => state.auth?.user?.claimedPlayer?.playerId);
    // Get free version data based on tab type
    const getFreeVersionData = useMemo(() => {
        const allPlayers = mockData.players;
        const { TOP_ONE_HUNDRED, CLOSE_IN_RANK } = LeaderboardTabTypes;
        const currentPlayerProfile = {
            playerId: currentProfile?.id,
            firstName: currentProfile?.firstName,
            lastName: currentProfile?.lastName,
            ratingDisplay: currentProfile?.singlesUtrDisplay,
            location: currentProfile?.location?.display,
        };

        if (currentTab === CLOSE_IN_RANK) {
            if (currentPlayerProfile?.playerId) {
                // Get 5 players before and 5 players after
                const singlesPlayersAbove = allPlayers.slice(0, 5);
                const singlesPlayersBelow = allPlayers.slice(5, 10);
                const doublesPlayersAbove = allPlayers.slice(15, 20);
                const doublesPlayersBelow = allPlayers.slice(20, 25);
                const currentPlayerProfileDoubles = {
                    ...currentPlayerProfile,
                    ratingDisplay: currentProfile?.doublesUtrDisplay,
                };

                // Create final list with current user always at index 5
                const finalSinglesList = [...singlesPlayersAbove, currentPlayerProfile, ...singlesPlayersBelow];
                const finalDoublesList = [...doublesPlayersAbove, currentPlayerProfileDoubles, ...doublesPlayersBelow];

                return {
                    singles: showSingles ? { players: finalSinglesList } : { players: [] },
                    doubles: showDoubles ? { players: finalDoublesList } : { players: [] },
                };
            }

            return {
                singles: showSingles ? { players: allPlayers.slice(0, 10) } : { players: [] },
                doubles: showDoubles ? { players: allPlayers.slice(20, 30) } : { players: [] },
            };
        }

        if (currentTab === TOP_ONE_HUNDRED) {
            // Top 100: show 50 players
            return {
                singles: showSingles ? { players: allPlayers.slice(0, 50) } : { players: [] },
                doubles: showDoubles ? { players: allPlayers.slice(49, 100) } : { players: [] },
            };
        }
    }, [currentTab, currentProfile, showSingles, showDoubles]);

    // When isPowerUser is true, use real API results; otherwise use mock data
    const displayResults = isPowerUser ? { singles, doubles } : getFreeVersionData;
    const { singles: displaySingles, doubles: displayDoubles } = displayResults || {};

    const { checkGate, featureGates } = useStatsig();
    const savedBookmarkEnabled = checkGate(featureGates.SAVED_PLAYER_LIST_ROLLOUT);
    // Probe only for a Power viewer with the flag on. Free viewers see a greyed bookmark on the mock
    // rows and make no saved-status call. Only the active view renders, so this hydrates the ids for
    // the current Close-in-Rank / Top-100 view; ids are de-duped across the Singles + Doubles columns.
    const savedBookmarkFetchEnabled = savedBookmarkEnabled && !!isPowerUser;
    // Never render (or probe) a save bookmark on the viewer's own row — you can't save yourself, and the
    // profile header on this same page hides its own bookmark the same way. This gates on the logged-in
    // user's playerId, not isProfilePlayer (which marks the profile being viewed, whose row on someone
    // else's profile is a legitimate save target).
    const isOwnRow = (player) => isSamePlayer(player?.playerId, currentUserPlayerId);
    const playerBookmarkIds = savedBookmarkFetchEnabled
        ? collectDistinctPlayerIds(displaySingles?.players, displayDoubles?.players)
            .filter((id) => !isSamePlayer(id, currentUserPlayerId))
        : [];
    const savedStatus = usePlayerSavedStatusBatch(playerBookmarkIds, { enabled: savedBookmarkFetchEnabled });

    const buildSavedBookmark = (player) => ((savedBookmarkEnabled && !isOwnRow(player)) ? {
        enabled: true,
        playerId: player?.playerId,
        playerName: [player?.firstName, player?.lastName].filter(Boolean).join(' ') || undefined,
        ...savedStatus.getRowStatus(player?.playerId),
        onSaveSuccess: savedStatus.handleSaveSuccess,
        onRemoveSuccess: savedStatus.handleRemoveSuccess,
    } : undefined);

    const hasSingles = displaySingles?.players?.length > 0;
    const hasDoubles = displayDoubles?.players?.length > 0;
    const hasBoth = hasSingles && hasDoubles;
    const showColumnHeader = !isMobileWidth || (isMobileWidth && !hasBoth);

    // Only use toggle state on mobile when both exist
    // Default to singles if available, otherwise doubles
    const [selectedView, setSelectedView] = useState(() => {
        if (rankingType === LeaderboardParamValues.SINGLES_TENNIS) {
            return SINGLES;
        }
        if (rankingType === LeaderboardParamValues.DOUBLES_TENNIS) {
            return DOUBLES;
        }
        return SINGLES;
    });

    // Update selected view only when results change from having one to having both (or vice versa)
    useEffect(() => {
        // Only reset if we don't have both types (single type scenario)
        if (!hasBoth) {
            if (rankingType === LeaderboardParamValues.SINGLES_TENNIS) {
                setSelectedView(SINGLES);
            } else if (rankingType === LeaderboardParamValues.DOUBLES_TENNIS) {
                setSelectedView(DOUBLES);
            }
        }
        // If hasBoth is true, don't reset - let user's selection persist
    }, [rankingType, hasBoth]);

    return (
        <div className={styles.resultsContainer}>
            {/* Toggle buttons - only render if both exist, CSS handles mobile/desktop visibility */}
            {hasBoth && (
                <div className={styles.singlesDoublesToggleContainer}>
                    <div className={styles.singlesDoublesToggle}>
                        <button
                            type="button"
                            onClick={() => setSelectedView(SINGLES)}
                            className={`${styles.singlesDoublesToggleButton} ${selectedView === SINGLES ? styles.singlesDoublesToggleButtonSelected : ''}`}
                            data-testid="profile.leaderboard.results.singles-btn"
                        >
                            <Img src="/img/v3/icons/icon-singles-new.svg" alt="Singles" />
                            <span>Singles</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setSelectedView(DOUBLES)}
                            className={`${styles.singlesDoublesToggleButton} ${selectedView === DOUBLES ? styles.singlesDoublesToggleButtonSelected : ''}`}
                            data-testid="profile.leaderboard.results.doubles-btn"
                        >
                            <Img src="/img/v3/icons/icon-doubles-new.svg" alt="Doubles" />
                            <span>Doubles</span>
                        </button>
                    </div>
                </div>
            )}
            <div className={`${styles.resultsColumnsWrapper} ${!hasBoth && !isMobileWidth ? styles.resultsColumnsWrapperCentered : ''}`}>
                {/* Singles column - show on desktop always, on mobile only if selected or only singles exists */}
                {(!isMobileWidth || !hasBoth || selectedView === SINGLES) && (
                    <div className={styles.resultListContainer}>
                        {hasSingles && (
                            <>
                                {showColumnHeader && (
                                    <div className={styles.resultListHeaderItem}>
                                        <Img src="/img/v3/icons/icon-singles-new.svg" alt="Singles" />
                                        <p className={styles.resultListHeaderItemText}>Singles</p>
                                    </div>
                                )}
                                {displaySingles?.players?.map((player, index) => {
                                    const isProfilePlayer = isSamePlayer(player.playerId, currentProfile?.id);
                                    const rowKey = player.playerId ?? index;
                                    return (
                                        <ResultCard
                                            key={rowKey}
                                            player={player}
                                            isProfilePlayer={isProfilePlayer}
                                            isPowerUser={isPowerUser}
                                            savedBookmark={buildSavedBookmark(player)}
                                            bookmarkColumnActive={savedBookmarkEnabled}
                                            data-testid={`profile.leaderboard.results.singles.row-${rowKey}`}
                                        />
                                    );
                                })}
                            </>
                        )}
                    </div>
                )}
                {/* Doubles column - show on desktop always, on mobile only if selected or only doubles exists */}
                {(!isMobileWidth || !hasBoth || selectedView === DOUBLES) && (
                    <div className={styles.resultListContainer}>
                        {hasDoubles && (
                            <>
                                {showColumnHeader && (
                                    <div className={styles.resultListHeaderItem}>
                                        <Img src="/img/v3/icons/icon-doubles-new.svg" alt="Doubles" />
                                        <p className={styles.resultListHeaderItemText}>Doubles</p>
                                    </div>
                                )}
                                {displayDoubles?.players?.map((player, index) => {
                                    const isProfilePlayer = isSamePlayer(player.playerId, currentProfile?.id);
                                    const rowKey = player.playerId ?? index;
                                    return (
                                        <ResultCard
                                            key={rowKey}
                                            player={player}
                                            isProfilePlayer={isProfilePlayer}
                                            isPowerUser={isPowerUser}
                                            savedBookmark={buildSavedBookmark(player)}
                                            bookmarkColumnActive={savedBookmarkEnabled}
                                            data-testid={`profile.leaderboard.results.doubles.row-${rowKey}`}
                                        />
                                    );
                                })}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

ResultList.propTypes = {
    results: PropTypes.shape({
        singles: PropTypes.objectOf(any),
        doubles: PropTypes.objectOf(any),
    }).isRequired,
    isPowerUser: PropTypes.bool.isRequired,
    currentTab: PropTypes.number.isRequired,
    rankingType: PropTypes.string,
    showDoubles: PropTypes.bool.isRequired,
    showSingles: PropTypes.bool.isRequired,
};

ResultList.defaultProps = {
    rankingType: null,
};

export default ResultList;
